import { ViewIds } from '@ardoq/api-types';
import styled from 'styled-components';
import { DoqType, ResponsiveDoqWithSpeechBubble } from '@ardoq/doq';
import { connect } from '@ardoq/rxbeach';
import { combineLatest } from 'rxjs';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';

const getDoqDataByViewId = (viewId?: ViewIds) => {
  switch (viewId) {
    case ViewIds.BLOCK_DIAGRAM:
    case ViewIds.MODERNIZED_BLOCK_DIAGRAM:
      return {
        doqType: DoqType.BLOCK_DIAGRAM_ERROR,
        viewName: 'Block Diagram',
      };
    case ViewIds.SWIMLANE:
      return { doqType: DoqType.SWIMLANE_ERROR, viewName: 'Swimlane' };
    default:
      return { doqType: null, viewName: '' };
  }
};

const getDoqAboveLimitWarningMessage = (
  i18nLocale: I18nLocale,
  viewName: string,
  isCanvasPrototype: boolean,
  isViewpointMode: boolean
): string => {
  const tooManyMessage = t(
    i18nLocale,
    'dataLimit:doqWarning.message.tooMany',
    { viewName },
    'There are too many components and references to show and this can make the {{viewName}} slow.'
  );

  if (isCanvasPrototype && isViewpointMode) {
    return `${tooManyMessage}\n \n ${t(
      i18nLocale,
      'dataLimit:doqWarning.message.smootherViewpoint',
      'For a smoother experience, select fewer context components, change your Viewpoint, or use a different view style.'
    )}`;
  }

  return `${tooManyMessage}\n \n ${t(
    i18nLocale,
    'dataLimit:doqWarning.message.smootherDataset',
    'For a smoother experience, try editing your dataset to show fewer components and references.'
  )}`;
};

const Container = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;

type DoqWarningProps = {
  viewId?: ViewIds;
  canBypass?: boolean;
  onContinueClick?: VoidFunction;
  isCanvasPrototype?: boolean;
  isViewpointMode?: boolean;
};

type DoqWarningConnectedProps = DoqWarningProps & WithI18nLocale;

const DoqWarningView = ({
  viewId,
  canBypass,
  onContinueClick,
  isCanvasPrototype = false,
  isViewpointMode = false,
  i18nLocale,
}: DoqWarningConnectedProps) => {
  const { doqType, viewName } = getDoqDataByViewId(viewId);
  if (!doqType) {
    return null;
  }

  const buttonLabel =
    canBypass && onContinueClick
      ? t(i18nLocale, 'dataLimit:doqWarning.continueAnyway', 'Continue anyway')
      : undefined;

  return (
    <Container>
      <ResponsiveDoqWithSpeechBubble
        doqType={doqType}
        message={getDoqAboveLimitWarningMessage(
          i18nLocale,
          viewName,
          isCanvasPrototype,
          isViewpointMode
        )}
        title={t(
          i18nLocale,
          'dataLimit:doqWarning.title',
          'This view may run slowly'
        )}
        buttonLabel={buttonLabel}
        onButtonClick={buttonLabel ? onContinueClick : undefined}
      />
    </Container>
  );
};

export const DoqWarning = connect(
  DoqWarningView,
  combineLatest({ i18nLocale: i18nLocale$ })
);
